<template>
    <div>
        <Card class="mb-4">
            <template v-slot:title>{{ __('stock-item.article_data_coverage') }}</template>
            <template v-slot:icon><i class="fas fa-compass"></i></template>
            <template v-slot:toolbar>
                <Link :href="$route('store.articles.issues', [$page.props.store.id])"
                      as="button"
                      class="btn btn-primary px-5">
                    <i class="fas fa-compass"/>
                    {{ __('stock-item.go_to_article_data_coverage') }}
                </Link>
            </template>
            <div class="d-flex align-items-center justify-content-start flex-column flex-md-row">
                <PieChart
                    height="350px"
                    type="pie"
                    :chart-labels="[ __('stock-item.complete_dataset'), __('stock-item.missing_article_data')]"
                    :series-data="[contentIssueCounts.total_stocks - contentIssueCounts.missing_article_count, contentIssueCounts.missing_article_count]"/>
                <div>
                    <div class="text-lg font-weight-bold">{{ __('stock-item.article_with_complete_dataset') }}</div>
                    <p>{{ __('stock-item.article_with_required_information') }}</p>
                    <div class="text-lg font-weight-bold">{{ __('stock-item.article_with_incomplete_dataset') }}</div>
                    <div v-html="__('stock-item.required_article_data_list_info')"></div>
                </div>
            </div>
        </Card>

        <Toolbar>
            <!-- RIGHT -->
            <template v-slot:right>
                <ToolbarLink :href="$route('article.create')">
                    <i class="fas fa-plus-square"/>
                    {{ __('actions.article.create') }}
                </ToolbarLink>
            </template>
        </Toolbar>

        <InventoryRefreshProgress :store-uuid="storeUuid"
                                  :init-refresh-chain="amazonInventoryRefreshChain"
        />

        <SynchronisationSettingsCard v-if="showChannelActions"
                                     class="my-3"
                                     :selected-channel="selectedChannel"
                                     :sync-enabled="syncEnabled"
                                     :store="store"
        />

        <Card>
            <template v-slot:icon>
                <div v-if="loading" class="spinner spinner-primary mr-5"/>
                <i v-else class="fas fa-list"/>
            </template>
            <template v-slot:title>
                {{ __('navigation.index_article') }}
            </template>
            <template v-slot:toolbar>
                <div class="d-flex flex-lg-row flex-column align-items-center mr-5">
                    <div class="mr-2 text-nowrap">
                        {{ __('actions.select_channel') }}:
                    </div>
                    <select class="form-control" @change="channelChanged">
                        <option v-for="channel in selectableChannels"
                                :value="channel.id"
                                :selected="selectedChannel === channel.id"
                                v-text="channel.name"
                        />
                    </select>
                </div>
                <div class="d-flex flex-lg-row flex-column align-items-center mr-5">
                    <div class="mr-2 text-nowrap">
                        {{ __('general.thing.select', {thing: __('stock-item.missing_data')}) }}:
                    </div>
                    <select class="form-control" @change="issueChanged">
                        <option v-for="issue in selectableIssues"
                                :value="issue.id"
                                :selected="selectedIssue === issue.id"
                                v-text="issue.name"
                        />
                    </select>
                </div>
                <div class="flex-grow-1 d-flex flex-lg-row flex-column align-items-center">
                    <div class="mr-2 text-nowrap">
                        {{ __('actions.search') }}:
                    </div>
                    <SearchInput v-model="search"
                                 input-classes="form-control-sm"
                                 :placeholder="__('product.product_search_placeholder')"
                                 @input="onSearchChange"
                    />
                </div>
                <div class="d-flex flex-lg-row flex-column align-items-center mr-5">
                    <div class="mr-2 text-nowrap">
                        {{ __('actions.edit_prices') }}:
                    </div>
                    <ToggleModel v-model="editPriceEnabled"/>
                </div>
            </template>

            <div class="table-responsive">
                <table class="table table-hover table-vertical-center">
                    <thead>
                    <tr class="text-nowrap">
                        <th>#</th>
                        <th>
                            <TableSort field="name" :value="sort" @input="onSortChange">
                                {{ __('general.name') }}
                            </TableSort>
                        </th>
                        <th>
                            <TableSort field="ean" :value="sort" @input="onSortChange">
                                ID <span v-if="paginatedStocksWithArticles.total > 0">({{
                                    paginatedStocksWithArticles.data[0].identifier_type.toUpperCase()
                                }})</span>
                            </TableSort>
                        </th>
                        <th class="text-center">
                            <TableSort field="quantity" :value="sort" @input="onSortChange">
                                {{ __('general.quantity') }}
                            </TableSort>
                        </th>
                        <th class="text-center">
                            <TableSort field="price" :value="sort" @input="onSortChange">
                                {{ __('general.price') }}
                            </TableSort>
                        </th>
                        <th class="text-center">
                            <TableSort field="last_change_at" :value="sort" @input="onSortChange">
                                {{ __('general.stock_change') }}
                            </TableSort>
                        </th>
                        <th>{{ __('general.actions') }}</th>
                        <th v-if="showChannelActions">{{ __('product.channel_actions') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="stock in paginatedStocksWithArticles.data">
                        <td>
                            <img v-if="stock.article && stock.article.image"
                                 :src="stock.article.image"
                                 style="width: 64px; height: 64px; object-fit: contain;"
                                 class="rounded"
                            >
                            <div v-else style="width: 64px; height: 64px; background-color: #eee; color: gray;"
                                 class="d-flex flex-column justify-content-center text-center rounded">
                                <span>?</span>
                            </div>
                        </td>
                        <td>
                            <div class="row align-items-center flex-nowrap">
                                <Tooltip v-if="stock.content_issues && stock.content_issues.length > 0"
                                         position="top"
                                         class="p-4">
                                    <template #trigger>
                                        <i class="mr-3 text-warning cursor-pointer fas fa-info-circle fa-lg"></i>
                                    </template>
                                    <Badge v-for="(issue, index) in stock.content_issues"
                                           type="bg-light-warning text-warning my-4 font-weight-bold "
                                           :key="index">
                                        {{ __(`stock-item.${issue.issue}`) }}
                                    </Badge>
                                </Tooltip>

                                <div v-if="stock.article">{{ stock.article.name }}</div>
                                <Badge v-else type="bg-light-warning text-warning">
                                    {{ __('stock-item.missing_article_data') }}
                                </Badge>
                            </div>
                        </td>
                        <td>
                            {{ stock.identifier }}
                        </td>
                        <td>
                            <input type="text"
                                   class="form-control text-center"
                                   :class="{ 'text-muted': stock.quantity === 0 }"
                                   v-model="stock.quantity"
                                   style="max-width: 100px;"
                                   @keyup="e => removeInvalidCharacters(e)"
                                   @change="e => updateValue(e, stock.identifier_type, stock.identifier, 'quantity')">
                        </td>
                        <td>
                            <input type="text"
                                   class="form-control text-center"
                                   :class="{ 'text-muted': stock.price === 0 }"
                                   v-model="stock.price"
                                   style="max-width: 100px;"
                                   :disabled="!editPriceEnabled"
                                   @keyup="e => removeInvalidCharacters(e)"
                                   @change="e => updateValue(e, stock.identifier_type, stock.identifier, 'price')">
                        </td>
                        <td class="text-center">
                            <DateTime :timestamp="stock.last_change_at"
                                      :date-format="{year: 'numeric', month: 'short', day: 'numeric'}"
                            />
                        </td>
                        <td>
                            <Actions>
                                <ActionButton v-if="!stock.article"
                                              :href="$route('article.create', {ean: stock.ean})"
                                              icon="fas fa-plus"
                                              class="btn-light-primary"
                                              :title="__('actions.create')"/>
                                <ActionButton v-if="manuallyCreated(stock)"
                                              :href="$route('article.update.show', {ean: stock.ean})"
                                              icon="fas fa-pen"
                                              class="btn-hover-primary"
                                              :title="__('actions.edit')"/>
                            </Actions>
                        </td>
                        <td v-if="showChannelActions && stock.integrations">
                            <Actions>
                                <ToggleModel :title="__('product.modal_title')"
                                             :disabled="syncEnabled"
                                             :value="isActiveInSelectedChannel(stock)"
                                             @input="(enabled) => updateStockDistribution(stock, enabled)"/>
                                <ActionButton v-if="stock.link"
                                              :href="stock.link"
                                              icon="fab fa-amazon"
                                              class="btn-primary"
                                              :target-blank="true"
                                              :title="setLinkButtonTitle(selectedChannel)"/>
                            </Actions>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <Pagination :pagination="paginatedStocksWithArticles"
                        :inertia-options="{only: ['paginatedStocksWithArticles']}"
            />
        </Card>
    </div>
</template>

<script>
import DateTime from '@components/TableComponents/DateTime';
import Pagination from '@components/TableComponents/Pagination';
import Actions from '@components/TableComponents/Actions';
import ActionButton from '@components/TableComponents/ActionButton';
import Toggle from '@components/Toggle';
import ToggleModel from '@components/ToggleModel';
import InventoryRefreshProgress from '@components/Integrations/Amazon/InventoryRefreshProgress.vue';
import Toolbar from '@components/Toolbar';
import ToolbarLink from '@components/ToolbarLink';
import HelpButton from '@components/HelpButton';
import Card from '@components/Cards/Card';
import SearchInput from '@components/Form/SearchInput';
import StoreLayout from '@layouts/StoreLayout';
import {IntegrationCodeEnum} from '@generated/integrationCodeEnum';
import SynchronisationSettingsCard from '@components/Article/SynchronisationSettingsCard';
import TableSort from '@components/Table/TableSort';
import {ContentIssueEnum} from '@generated/contentIssueEnum';
import PieChart from '@components/PieChart.vue';
import ColumnChart from '@components/Charts/BarChart.vue';
import {Link} from '@inertiajs/vue2';
import Tooltip from '@components/Tooltip.vue';
import Badge from '@components/Badge.vue';
import {rewriteUrlNew} from '@composables/rewriteUrlNew';

export default {
    layout: StoreLayout,

    components: {
        Badge,
        Tooltip,
        ColumnChart,
        PieChart,
        TableSort,
        SynchronisationSettingsCard,
        SearchInput,
        Card,
        HelpButton,
        Toolbar,
        ToolbarLink,
        DateTime,
        Pagination,
        Actions,
        ActionButton,
        Toggle,
        ToggleModel,
        InventoryRefreshProgress,
        Link,
    },

    props: {
        stores: Array,
        paginatedStocksWithArticles: Object,
        initChannel: String,
        initSearch: String,
        storeUuid: String,
        page: {
            type: Number,
            default: 1,
        },
        syncEnabled: Boolean,
        amazonInventoryRefreshChain: Object,
        contentIssueCounts: {
            total_stocks: Number,
            missing_article_count: Number,
            missing_description_count: Number,
            missing_image_count: Number,
            missing_manufacturer_info_count: Number,
        },
        initIssue: String,
    },

    data() {
        return {
            loading: false,
            channels: [
                {
                    id: IntegrationCodeEnum.YouBuyDa,
                    name: this.__('product.whole_stock'),
                },
                {
                    id: IntegrationCodeEnum.AmazonBOPIS,
                    name: 'Amazon',
                },
                {
                    id: IntegrationCodeEnum.AmazonTODAY,
                    name: 'Amazon Today',
                },
            ],
            selectedChannel: this.initChannel,
            selectedIssue: this.initIssue,
            editPriceEnabled: false,
            search: this.initSearch.trim(),
            sort: {},
        };
    },

    methods: {
        updateValue(e, identifierType, identifier, column) {
            axios.post(this.$route('ajax.article.stock-items.update', [this.store.id]), {
                identifier_type: identifierType,
                identifier: identifier,
                field: column,
                value: e.srcElement.value,
            }).then(response => {
                if (response.status === 200) {
                    toastr.success(this.__('product.toast_update_value.success'));
                    e.srcElement.blur();
                }
            }).catch(errorResponse => {
                toastr.warning(this.__('product.toast_update_value.warning'));
            });
        },

        removeInvalidCharacters(e) {
            e.srcElement.value = e.srcElement.value.replace(',', '.').replace(/[^\d.]/, '');
        },

        onSortChange(params) {
            this.loading = true;
            this.sort = params;
            rewriteUrlNew('sort', params);
            this.$inertia.reload({
                data: {
                    page: 1,
                },
                only: ['paginatedStocksWithArticles'],
                onFinish: () => {
                    this.loading = false;
                },
            });
        },

        onSearchChange() {
            this.loading = true;
            this.$inertia.reload({
                data: {
                    page: 1,
                    search: this.search,
                },
                only: ['paginatedStocksWithArticles', 'search'],
                onFinish: () => {
                    this.loading = false;
                },
            });
        },

        manuallyCreated(row) {
            return row.hasOwnProperty('article') && row.article !== null && row.article.user_created;
        },

        channelChanged(e) {
            this.loading = true;
            this.selectedChannel = e.srcElement.value;
            this.$inertia.reload({
                data: {
                    page: 1,
                    channel: this.selectedChannel,
                },
                only: ['paginatedStocksWithArticles', 'syncEnabled'],
                onFinish: () => {
                    this.loading = false;
                },
            });
        },

        issueChanged(e) {
            this.loading = true;
            this.selectedIssue = e.srcElement.value;
            this.$inertia.reload({
                data: {
                    page: 1,
                    content_issue: this.selectedIssue,
                },
                only: ['paginatedStocksWithArticles', 'syncEnabled'],
                onFinish: () => {
                    this.loading = false;
                },
            });
        },

        isActiveInSelectedChannel(stock) {
            if (this.selectedChannel === IntegrationCodeEnum.YouBuyDa) {
                return true;
            }

            for (let integration of stock.integrations) {
                if (integration.integration === this.selectedChannel) {
                    return integration.active;
                }
            }

            return false;
        },

        setLinkButtonTitle(integrationLabel) {
            switch (integrationLabel) {
                case IntegrationCodeEnum.AmazonBOPIS :
                    return 'Artikel bei Amazon anschauen';

                default :
                    return 'Artikel anschauen';
            }
        },

        updateStockDistribution(stock, activate) {
            try {
                if (activate) {
                    axios.post(this.$route('integration-stocks.store'), {
                        stock_id: stock.id,
                        integration_id: this.selectedChannel,
                    }).then(response => {
                        for (let integration of stock.integrations) {
                            if (integration.integration === this.selectedChannel) {
                                integration.active = activate;
                            }
                        }
                        toastr.success(
                            this.__('product.toast_update_stock.success',
                                {'ean': stock.ean, 'channel': this.selectedChannel},
                            ));
                    });
                } else {
                    axios.post(this.$route('integration-stocks.delete'), {
                        stock_id: stock.id,
                        integration_id: this.selectedChannel,
                    }).then(response => {
                        for (let integration of stock.integrations) {
                            if (integration.integration === this.selectedChannel) {
                                integration.active = activate;
                            }
                        }
                        toastr.warning(
                            this.__('product.toast_update_stock.warning',
                                {'ean': stock.ean, 'channel': this.selectedChannel},
                            ));
                    });
                }
            } catch (error) {
                toastr.error(
                    this.__('product.toast_update_stock.error',
                        {'ean': stock.ean, 'channel': this.selectedChannel},
                    ));
            }
        },
    },

    computed: {
        IntegrationCodeEnum() {
            return IntegrationCodeEnum;
        },

        selectableIssues() {
            return [
                {id: null, name: 'Kein Filter'},
                {id: ContentIssueEnum.MISSING_MANUFACTURER_INFO, name: this.__(`stock-item.${ContentIssueEnum.MISSING_MANUFACTURER_INFO}`)},
                {id: ContentIssueEnum.MISSING_IMAGE, name: this.__(`stock-item.${ContentIssueEnum.MISSING_IMAGE}`)},
                {id: ContentIssueEnum.MISSING_ARTICLE, name: this.__(`stock-item.${ContentIssueEnum.MISSING_ARTICLE}`)},
                {id: ContentIssueEnum.MISSING_DESCRIPTION, name: this.__(`stock-item.${ContentIssueEnum.MISSING_DESCRIPTION}`)},
            ];
        },

        store() {
            for (let store of this.stores) {
                if (store.id === this.storeUuid) {
                    return store;
                }
            }
        },

        showChannelActions() {
            return this.selectedChannel !== IntegrationCodeEnum.YouBuyDa;
        },

        selectableChannels() {
            return this.channels.filter(channel => {
                return (this.store.hasOwnProperty(channel.id) && this.store[channel.id]) || channel.id === IntegrationCodeEnum.YouBuyDa;
            });
        },
    },
};
</script>
